<template>
  <span
    class="relative inline-flex items-center align-middle"
    @mouseenter="onOpen"
    @mouseleave="onClose"
    @focusin="onOpen"
    @focusout="onClose"
    @click="onToggleClick"
    @keydown.esc.stop.prevent="hide"
  >
    <!-- Trigger -->
    <span
      ref="triggerRef"
      class="inline-flex"
      :aria-describedby="isOpen ? tooltipId : undefined"
      :tabindex="triggerTabIndex"
    >
      <slot />
    </span>

    <!-- Panel (teleported to body to avoid clipping/stacking issues) -->
    <teleport to="body">
      <transition name="fade" appear>
        <div
          v-show="isOpen"
          ref="panelRef"
          :id="tooltipId"
          role="tooltip"
          :class="[
            'fixed z-[9999] pointer-events-none rounded-lg shadow-lg border px-2 py-1 select-none normal-case backdrop-blur-sm',
            'bg-white/70 text-gray-900 border-gray-200/40',
            'dark:bg-gray-900/70 dark:text-white dark:border-gray-700/40',
            'max-w-[calc(100vw-2rem)]',
            sizeClass
          ]"
          :style="panelStyle"
        >
          <div :class="['w-full whitespace-normal break-words leading-snug font-normal', alignClass]">
            <template v-if="$slots.content">
              <slot name="content" />
            </template>
            <template v-else>
              <div v-html="sanitizedContent" />
            </template>
          </div>
        </div>
      </transition>
    </teleport>
  </span>
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeUnmount, nextTick, computed } from 'vue'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

export default defineComponent({
  name: 'Tooltip',
  props: {
    content: { type: String, default: '' },
    placement: { type: String, default: 'top' }, // top | bottom | left | right
    width: { type: String, default: '16rem' }, // e.g. '16rem'
    openDelay: { type: Number, default: 100 },
    closeDelay: { type: Number, default: 100 },
    triggerTabIndex: { type: [Number, String], default: 0 },
    align: { type: String, default: 'center' }, // center | left | right
    size: { type: String, default: 'sm' }, // xs | sm | base | lg
  },
  setup(props) {
    const isOpen = ref(false)
    const openTimer = ref(null)
    const closeTimer = ref(null)
    const triggerRef = ref(null)
    const panelRef = ref(null)
    const tooltipId = `tt-${Math.random().toString(36).slice(2, 9)}`

    // Width handled inline to prevent Tailwind purge issues with dynamic classes
    const widthStyle = `min(${props.width}, calc(100vw - 2rem))`
    const alignClass = props.align === 'left' ? 'text-left' : props.align === 'right' ? 'text-right' : 'text-center'
    const sizeMap = { xs: 'text-xs', sm: 'text-sm', base: 'text-base', lg: 'text-lg' }
    const sizeClass = sizeMap[props.size] || 'text-xs'
    const sanitizedContent = computed(() => sanitizeHtml(props.content))

    const panelStyle = ref({ width: widthStyle })
    let scrollRafId = null

    const GUTTER_X = 16
    const GUTTER_Y = 8

    const rectsOverlap = (a, b) =>
      a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top

    const getHorizontalLeft = (tr, pr) => {
      if (props.align === 'left') return tr.left
      if (props.align === 'right') return tr.right - pr.width
      return tr.left + (tr.width - pr.width) / 2
    }

    const clampHorizontal = (left, prWidth, vw) => {
      if (left < GUTTER_X) left = GUTTER_X
      if (left + prWidth > vw - GUTTER_X) left = vw - GUTTER_X - prWidth
      return left
    }

    const computeCoords = (placement, tr, pr, vw, vh) => {
      let top = 0
      let left = 0

      switch (placement) {
        case 'bottom':
          top = tr.bottom + GUTTER_Y
          left = getHorizontalLeft(tr, pr)
          break
        case 'left':
          top = tr.top + (tr.height - pr.height) / 2
          left = tr.left - pr.width - GUTTER_X
          break
        case 'right':
          top = tr.top + (tr.height - pr.height) / 2
          left = tr.right + GUTTER_X
          break
        case 'top':
        default:
          top = tr.top - pr.height - GUTTER_Y
          left = getHorizontalLeft(tr, pr)
          break
      }

      left = clampHorizontal(left, pr.width, vw)
      if (top < GUTTER_Y) top = GUTTER_Y
      if (top + pr.height > vh - GUTTER_Y) top = vh - GUTTER_Y - pr.height

      return {
        top,
        left,
        tooltipRect: { top, left, right: left + pr.width, bottom: top + pr.height },
      }
    }

    const resolvePlacement = (preferred, tr, pr, vh) => {
      const needed = pr.height + GUTTER_Y
      const spaceTop = tr.top - GUTTER_Y
      const spaceBottom = vh - tr.bottom - GUTTER_Y

      if (preferred === 'top') {
        if (spaceTop < needed && spaceBottom >= needed) return 'bottom'
        if (spaceTop < needed && spaceBottom < needed) {
          return spaceBottom >= spaceTop ? 'bottom' : 'top'
        }
      }

      if (preferred === 'bottom') {
        if (spaceBottom < needed && spaceTop >= needed) return 'top'
        if (spaceBottom < needed && spaceTop < needed) {
          return spaceTop >= spaceBottom ? 'top' : 'bottom'
        }
      }

      return preferred
    }

    const show = () => {
      clearTimeout(closeTimer.value)
      openTimer.value = setTimeout(() => {
        isOpen.value = true
        nextTick(() => {
          updatePosition()
          bindOutside()
          bindResize()
          bindScroll()
        })
      }, props.openDelay)
    }

    const hide = () => {
      clearTimeout(openTimer.value)
      closeTimer.value = setTimeout(() => {
        isOpen.value = false
        unbindOutside()
        unbindResize()
        unbindScroll()
      }, props.closeDelay)
    }

    const onOpen = () => show()
    const onClose = (event) => {
      const trigger = triggerRef.value
      const related = event?.relatedTarget
      if (trigger && related instanceof Node && trigger.contains(related)) return
      hide()
    }
    const onToggleClick = (e) => {
      // Prefer tap/click for mobile; toggle state
      e.stopPropagation()
      if (isOpen.value) hide()
      else show()
    }

    const onDocClick = (e) => {
      const t = triggerRef.value
      const p = panelRef.value
      if (!t || !p) return hide()
      if (!t.contains(e.target) && !p.contains(e.target)) hide()
    }

    const bindOutside = () => document.addEventListener('pointerdown', onDocClick)
    const unbindOutside = () => document.removeEventListener('pointerdown', onDocClick)

    const onResize = () => {
      if (isOpen.value) schedulePositionUpdate()
    }
    const bindResize = () => window.addEventListener('resize', onResize)
    const unbindResize = () => window.removeEventListener('resize', onResize)

    const schedulePositionUpdate = () => {
      if (scrollRafId !== null) cancelAnimationFrame(scrollRafId)
      scrollRafId = requestAnimationFrame(() => {
        scrollRafId = null
        updatePosition()
      })
    }

    const onScroll = () => {
      if (isOpen.value) schedulePositionUpdate()
    }
    const bindScroll = () => window.addEventListener('scroll', onScroll, true)
    const unbindScroll = () => window.removeEventListener('scroll', onScroll, true)

    const updatePosition = () => {
      const t = triggerRef.value
      const p = panelRef.value
      if (!t || !p) return
      const tr = t.getBoundingClientRect()
      const pr = p.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight

      const triggerRect = { top: tr.top, left: tr.left, right: tr.right, bottom: tr.bottom }
      let placement = resolvePlacement(props.placement, tr, pr, vh)
      let { top, left, tooltipRect } = computeCoords(placement, tr, pr, vw, vh)

      if (rectsOverlap(tooltipRect, triggerRect)) {
        const flipped = placement === 'top' ? 'bottom' : placement === 'bottom' ? 'top' : placement
        if (flipped !== placement) {
          const flippedCoords = computeCoords(flipped, tr, pr, vw, vh)
          if (!rectsOverlap(flippedCoords.tooltipRect, triggerRect)) {
            top = flippedCoords.top
            left = flippedCoords.left
          }
        }
      }

      panelStyle.value = { top: `${top}px`, left: `${left}px`, width: widthStyle }
    }

    onMounted(() => {})
    onBeforeUnmount(() => {
      if (scrollRafId !== null) cancelAnimationFrame(scrollRafId)
      unbindOutside()
      unbindResize()
      unbindScroll()
    })

    return {
      isOpen,
      tooltipId,
      triggerRef,
      panelRef,
      
      sizeClass,
      alignClass,
      panelStyle,
      onOpen,
      onClose,
      onToggleClick,
      hide,
      updatePosition,
      sanitizedContent,
    }
  }
})
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 120ms ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
